// pages/StandaloneAlbum.js
// Standalone album page - requires login, with optional password protection
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiAlertCircle, FiSmartphone, FiLogIn } from 'react-icons/fi';
import { useAuth } from '../services/auth';
import { getAlbumBySlug, checkEventSlug } from '../services/albumService';
import AlbumPasswordModal from '../components/AlbumPasswordModal';
import StandaloneAlbumView from '../components/StandaloneAlbumView';
import '../styles/StandaloneAlbum.scss';

const StandaloneAlbum = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mediaCount, setMediaCount] = useState({ photos: 0, videos: 0 });
  const [isEventLinked, setIsEventLinked] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      loadAlbum();
    }
  }, [slug, authLoading, user]);

  const loadAlbum = async () => {
    try {
      setLoading(true);
      setError(null);

      const albumData = await getAlbumBySlug(slug);

      if (!albumData) {
        // Check if it's an event slug
        const eventData = await checkEventSlug(slug);
        if (eventData) {
          setIsEventLinked(true);
          return;
        }
        setError('Album not found');
        return;
      }

      // Check if album is linked to an event
      if (albumData.link_type === 'event') {
        setIsEventLinked(true);
        return;
      }

      // Check if album is expired
      if (albumData.storage_expires_at) {
        const expiryDate = new Date(albumData.storage_expires_at);
        if (expiryDate < new Date()) {
          setError('This album has expired and is no longer available.');
          return;
        }
      }

      setAlbum(albumData);

      // Check if password is required
      if (albumData.has_password) {
        setShowPasswordModal(true);
      } else {
        setPasswordVerified(true);
      }
    } catch (err) {
      console.error('Error loading album:', err);
      setError('Failed to load album');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordVerified = () => {
    setPasswordVerified(true);
    setShowPasswordModal(false);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: album?.name || 'Album',
          text: `Check out this album!`,
          url,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      } catch (err) {
        // Fallback
        const textarea = document.createElement('textarea');
        textarea.value = url;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('Link copied to clipboard!');
      }
    }
  };

  const getMediaCountText = () => {
    const { photos, videos } = mediaCount;
    if (videos === 0) return `${photos} ${photos === 1 ? 'Photo' : 'Photos'}`;
    if (photos === 0) return `${videos} ${videos === 1 ? 'Video' : 'Videos'}`;
    return `${photos} ${photos === 1 ? 'Photo' : 'Photos'}, ${videos} ${videos === 1 ? 'Video' : 'Videos'}`;
  };

  // Auth loading state
  if (authLoading) {
    return (
      <div className="standalone-album-loading">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  // Not logged in - show login prompt
  if (!user) {
    return (
      <div className="standalone-album-login-prompt">
        <div className="login-prompt-content">
          <span className="login-prompt-icon"><FiLogIn /></span>
          <h2>Sign in to view this album</h2>
          <p>You need to be logged in to access this album. Please sign in or create an account to continue.</p>
          <button
            className="login-btn"
            onClick={() => navigate('/login', { state: { from: location.pathname } })}
          >
            Sign In
          </button>
          <button
            className="signup-btn"
            onClick={() => navigate('/login', { state: { from: location.pathname, signup: true } })}
          >
            Create Account
          </button>
        </div>
      </div>
    );
  }

  // Loading album state
  if (loading) {
    return (
      <div className="standalone-album-loading">
        <div className="spinner" />
        <p>Loading album...</p>
      </div>
    );
  }

  // Event-linked album state
  if (isEventLinked) {
    return (
      <div className="standalone-album-event-linked">
        <div className="event-linked-content">
          <span className="event-linked-icon"><FiSmartphone /></span>
          <h2>This album is linked to an event</h2>
          <p>To view this album, please open it in the Invits app.</p>
          <a
            href="https://invits.co/app"
            className="open-app-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Invits App
          </a>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="standalone-album-error">
        <span className="error-icon"><FiAlertCircle /></span>
        <h2>{error}</h2>
        <p>The album you're looking for may have been removed or is no longer available.</p>
      </div>
    );
  }

  // Album not found
  if (!album) {
    return null;
  }

  // Password required but not verified yet
  if (album.has_password && !passwordVerified) {
    return (
      <div className="standalone-album-page">
        <AlbumPasswordModal
          album={album}
          onVerified={handlePasswordVerified}
          onClose={() => navigate('/')}
        />
      </div>
    );
  }

  return (
    <div className="standalone-album-page">
      {/* Header */}
      <div className="standalone-album-header">
        <div className="header-content">
          <button className="header-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>

          <div className="header-title-container">
            <h1>{album.name}</h1>
            <div className="header-subtitle">{getMediaCountText()}</div>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={handleShare}>
              <FiShare2 />
            </button>
          </div>
        </div>
      </div>

      {/* Album Content */}
      <StandaloneAlbumView
        albumId={album.id}
        album={album}
        onMediaCountChange={setMediaCount}
      />
    </div>
  );
};

export default StandaloneAlbum;
